import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import FormControl from '@mui/material/FormControl'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import MuiCard from '@mui/material/Card'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import MicrosoftSSOLogin from './MicrosoftSSO'
import {
  setAppetiteData,
  selectedApplicationData
} from '../../store/ApplicationSlice'

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px'
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px'
  })
}))

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  marginTop: '10vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))'
    })
  }
}))

export default function SignInForm (props) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const [passwordError, setPasswordError] = useState(false)
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('')
  const [loginError, setLoginError] = useState('')
  const [microsofUser, setMicrosoftUser] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (microsofUser) {
      login(microsofUser.email, microsofUser.password)
    }
  }, [microsofUser])

  const handleClickOpen = () => {
    // logic to handle forgot password
  }

  const handleSubmit = event => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
  }

  const login = async (email, password) => {
    setLoading(true)
    try {
      const formData = new URLSearchParams()
      formData.append('username', email)
      formData.append('password', password)

      const response = await axios.post(
        'https://insureai-intake-dev-1074099157700.us-central1.run.app/insureai/ml/v1/login',
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json'
          }
        }
      )

      const { access_token, refresh_token } = response.data
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      await fetchApplicationData(access_token, refresh_token)
      navigate('/app/details')
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail || 'Login failed. Please try again.'
      setLoginError(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const fetchApplicationData = async (accessToken, refreshToken) => {
    const headers = {
      'X-Access-Token': accessToken,
      'X-Refresh-Token': refreshToken,
      Accept: 'application/json'
    }
    try {
      const response = await axios.get(
        'https://insureai-intake-dev-1074099157700.us-central1.run.app/insureai/ml/intake/applications/v2/?skip=0&limit=0',
        { headers }
      )

      dispatch(setAppetiteData(response.data))
      dispatch(selectedApplicationData(response.data[0]))
    } catch (error) {
      console.error('Error fetching application data:', error)
    }
  }

  const validateInputs = () => {
    const email = document.getElementById('email')
    const password = document.getElementById('password')

    let isValid = true

    if (!email.value) {
      setEmailError(true)
      setEmailErrorMessage('Please enter a valid email address.')
      isValid = false
    } else {
      setEmailError(false)
      setEmailErrorMessage('')
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true)
      setPasswordErrorMessage('Password must be at least 6 characters long.')
      isValid = false
    } else {
      setPasswordError(false)
      setPasswordErrorMessage('')
    }

    if (isValid) {
      login(email.value, password.value)
    }
  }

  return (
    <>
      <SignInContainer direction='column' justifyContent='space-between'>
        <Card variant='outlined'>
          <Typography
            component='h1'
            variant='h4'
            sx={{
              width: '100%',
              fontSize: 'clamp(2rem, 10vw, 2.15rem)',
              textAlign: 'center'
            }}
          >
            Sign In
          </Typography>

          {loginError && (
            <Typography color='error' sx={{ textAlign: 'center' }}>
              {loginError}
            </Typography>
          )}

          <Box
            component='form'
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2
            }}
          >
            <FormControl>
              <FormLabel htmlFor='email'>Username</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id='email'
                type='email'
                name='email'
                placeholder='your@email.com'
                autoComplete='email'
                autoFocus
                required
                fullWidth
                variant='outlined'
                color={emailError ? 'error' : 'primary'}
                sx={{ ariaLabel: 'email' }}
              />
            </FormControl>
            <FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormLabel htmlFor='password'>Password</FormLabel>
                <Link
                  component='button'
                  onClick={handleClickOpen}
                  variant='body2'
                  sx={{ alignSelf: 'baseline' }}
                >
                  Forgot your password?
                </Link>
              </Box>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name='password'
                placeholder='••••••'
                type='password'
                id='password'
                autoComplete='current-password'
                autoFocus
                required
                fullWidth
                variant='outlined'
                color={passwordError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox value='remember' color='primary' />}
              label='Remember me'
            />

            <Button
              type='submit'
              variant='contained'
              onClick={validateInputs}
              style={{ backgroundColor: '#FF4D00', color: '#fff' }}
            >
              Sign in
            </Button>

            <Typography sx={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <span>
                <Link
                  href='/material-ui/getting-started/templates/sign-in/'
                  variant='body2'
                  sx={{ alignSelf: 'center' }}
                >
                  Sign up
                </Link>
              </span>
            </Typography>
          </Box>
          <MicrosoftSSOLogin setMicrosoftUser={setMicrosoftUser} />
        </Card>
      </SignInContainer>

      <Backdrop
        open={loading}
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: '#ea580c'
          }}
        />
      </Backdrop>
    </>
  )
}
